/**
 * masonry.ts — Brick and tile as a surface, not as scenery.
 *
 * The town's roofs and brick walls are the two biggest flat areas in the game,
 * and flat clay reads as a blank slab. Both get a running-bond pattern — but as
 * a **texture painted onto the shell**, with the windows, doors and drainpipes
 * as separate meshes standing on top of it. That's the order a real building
 * goes together in, and it's what this module exists to make possible.
 *
 * The first attempt built each tile as its own little box. Two problems, and
 * they're the reason for the rewrite: a box only shows up if it stands *off*
 * the surface, so the roof ended up looking gritted rather than tiled; and it
 * cost about 150k triangles to say something a 256-pixel canvas says better.
 *
 * ## Three things this has to get right
 *
 * 1. **The pattern must tile seamlessly.** The canvas is drawn as a whole number
 *    of bricks by a whole number of courses, and any brick crossing the right
 *    edge is drawn again at the left. Miss that and every repeat shows a seam.
 * 2. **It must land at a fixed world size.** UV space is normalised per face, so
 *    the same texture on a wide wall and a narrow one gives two different brick
 *    sizes. `systems/coursing.ts` works out the repeat counts; `paintBox` bakes
 *    them into the geometry's own UVs rather than setting `texture.repeat`,
 *    because a texture is shared between meshes and its repeat is not per-face.
 * 3. **It must survive the rounded corners.** `RoundedBoxGeometry` parameterises
 *    its UVs by *arc length*, so a scaled UV carries the brick around the curve
 *    at a constant size instead of smearing it. This is the one piece of luck in
 *    the whole exercise — it's why the corners need no special handling, where
 *    the old instanced courses had to stop dead short of `clayRadius()`.
 *
 * Textures are cached per colour and shared. Never dispose one.
 */
import * as THREE from 'three';
import { BUILDINGS, PAVEMENT } from '../config/constants';
import { MASONRY, ROOFS } from '../config/palette';
import { boxRepeats, turnedBoxRepeats, hipTriangles } from '../systems/coursing';
import { clayGeometry } from './clay';
import { hex, patternTexture, patternedClay, type SurfaceTexture } from './textures';
import { shadeHex } from '../utils/color';

/** How many pattern cells the canvas covers. Even, so a half-offset row wraps. */
const CELLS_X = 4;
const CELLS_Y = 8;
/** Pixels per cell. Enough that a brick still has a crisp edge close up. */
const CELL_PX = 64;

/**
 * A pattern's colour map and matching relief. The generic machinery — the cache,
 * the canvas→texture step, the clay material — lives in `textures.ts`, which the
 * ground patterns share; what stays here is the running bond itself and the UV
 * painting that wraps it round a building.
 */
export type MasonryTexture = SurfaceTexture;

/** Proportions of one masonry pattern, as fractions of a cell. */
interface Pattern {
  /** Joint width, as a fraction of the cell. */
  joint: number;
  /** How much lighter a unit's face is than the joint around it. */
  lighten: number;
  /** Corner rounding on a unit, as a fraction of the cell. */
  round: number;
}

// Keep `round` low. Anything above about 0.15 stops reading as a rectangle and
// starts reading as a fish scale, which turns a slate roof into a dragon.
const BRICK: Pattern = { joint: 0.13, lighten: MASONRY.BRICK_FACE_LIGHTEN, round: 0.14 };
const TILE: Pattern = { joint: 0.14, lighten: ROOFS.TILE_FACE_LIGHTEN, round: 0.12 };
const PAVING: Pattern = { joint: 0.16, lighten: MASONRY.PAVING_FACE_LIGHTEN, round: 0.06 };
// The one place the "keep `round` low" rule above is meant to be broken. A sett
// really is a rounded stone, so the fish-scale reading that ruins a slate roof
// is exactly right here — it is what stops a cobbled lane looking like paving
// in a different colour. The joint is wide to match: mortar between setts is a
// far bigger share of the surface than between slabs.
const COBBLE: Pattern = { joint: 0.22, lighten: MASONRY.COBBLE_FACE_LIGHTEN, round: 0.36 };

/** Brickwork in a wall of this colour. Shared and cached — do not dispose. */
export function brickTexture(color: number): MasonryTexture {
  return masonryTexture(color, BRICK, 'brick');
}

/** Tiling on a roof of this colour. Shared and cached — do not dispose. */
export function tileTexture(color: number): MasonryTexture {
  return masonryTexture(color, TILE, 'tile');
}

/**
 * Paving slabs on a footway. Shared and cached — do not dispose.
 *
 * The same running bond as a brick wall, and that is the point: a pavement *is*
 * masonry, laid flat. It replaces the thin joint-line slabs the footways used to
 * be striped with, which were hundreds of extra meshes drawing what one canvas
 * draws better — the exact trade the walls made when the tiles stopped being
 * geometry. A wider joint than brickwork, because a paving joint really is
 * wider relative to the slab.
 */
export function pavingTexture(color: number): MasonryTexture {
  return masonryTexture(color, PAVING, 'paving');
}

/**
 * Cobble setts on a pedestrian lane. Shared and cached — do not dispose.
 *
 * The same running bond again, laid smaller and rounder. Where a lane meets a
 * footway the two surfaces butt up end-on, so what separates them has to survive
 * being seen side by side: the colour difference alone is weak at this camera,
 * and it is the **unit size** that does the work — four setts to a paving slab.
 */
export function cobbleTexture(color: number): MasonryTexture {
  return masonryTexture(color, COBBLE, 'cobble');
}

function masonryTexture(color: number, pattern: Pattern, kind: string): MasonryTexture {
  return patternTexture(`${kind}:${color}`, (relief) => drawPattern(color, pattern, relief));
}

/**
 * Draw one tile of the running bond.
 *
 * `relief` swaps the palette for greys: the same shapes, read as a bump map, so
 * the units catch a highlight along their top edge and sit in their bed rather
 * than looking printed on. It's the last thing keeping the surface feeling like
 * pressed clay now that the tiles aren't real geometry.
 */
function drawPattern(color: number, pattern: Pattern, relief: boolean): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = CELLS_X * CELL_PX;
  canvas.height = CELLS_Y * CELL_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // The joint is the surface itself showing through between the units, so the
  // background is the wall's own colour and the units are a shade lighter.
  const back = relief ? '#000000' : hex(color);
  const face = relief ? '#ffffff' : hex(shadeHex(color, pattern.lighten));

  ctx.fillStyle = back;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = face;

  const joint = pattern.joint * CELL_PX;
  const unitW = CELL_PX - joint;
  const unitH = CELL_PX - joint;
  const radius = pattern.round * CELL_PX;

  for (let row = 0; row < CELLS_Y; row++) {
    // Every other course slides half a unit along. That offset is the whole
    // trick: without it the cross joints line up into continuous vertical
    // seams and the wall reads as a grid of tiles rather than as brickwork.
    const shift = (row % 2) * CELL_PX * 0.5;
    const y = row * CELL_PX + joint / 2;
    // One extra column, drawn past the right edge and again wrapped round to
    // the left, so the shifted rows join up across the seam.
    for (let col = -1; col <= CELLS_X; col++) {
      const x = col * CELL_PX + shift + joint / 2;
      roundRect(ctx, x, y, unitW, unitH, radius);
      roundRect(ctx, x - canvas.width, y, unitW, unitH, radius);
    }
  }
  return canvas;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, Math.min(r, w / 2, h / 2));
  ctx.fill();
}

/**
 * A matte clay material carrying a masonry pattern.
 *
 * Same recipe as `clayMaterial` — see the note there about the faint self-glow —
 * with the pattern as both colour and relief. Cached alongside the texture, so
 * every wall of the same brick shares one material and one draw call's worth of
 * state.
 */
export function masonryMaterial(
  color: number,
  texture: MasonryTexture,
  bumpScale: number,
): THREE.MeshStandardMaterial {
  return patternedClay(color, texture, bumpScale);
}

/**
 * Rescale a rounded box's UVs so its pattern tiles at a fixed world size.
 *
 * Each of the six faces runs u and v from 0 to 1 regardless of how big it is,
 * so this multiplies them up by that face's own repeat count. The geometry must
 * be unshared — see `courseGeometry`.
 *
 * `swap` exchanges u and v on every face, which turns the pattern through a
 * right angle. The gable slopes need it: the same slab geometry is used for a
 * ridge running along x and one running along z, and the courses have to follow
 * the ridge either way.
 */
export function paintBox(
  geo: THREE.BufferGeometry,
  w: number,
  h: number,
  d: number,
  cellW: number,
  cellH: number,
  swap = false,
): void {
  const uv = geo.getAttribute('uv');
  const reps = swap
    ? turnedBoxRepeats(w, h, d, cellW, cellH)
    : boxRepeats(w, h, d, cellW, cellH);
  // RoundedBoxGeometry lays its six faces out in equal, contiguous runs of
  // vertices, in the order +x −x +y −y +z −z. Same order, same arithmetic as
  // the geometry's own constructor uses to assign the UVs in the first place.
  const perFace = uv.count / 6;
  const order = [reps.x, reps.x, reps.y, reps.y, reps.z, reps.z];

  for (let i = 0; i < uv.count; i++) {
    const face = order[Math.min(5, Math.floor(i / perFace))];
    // Turning the pattern reads u from the face's v axis and vice versa; the
    // counts already come back in that order, so the scaling is the same both
    // ways.
    const u = swap ? uv.getY(i) : uv.getX(i);
    const v = swap ? uv.getX(i) : uv.getY(i);
    uv.setXY(i, u * face.u, v * face.v);
  }
  uv.needsUpdate = true;
}

/**
 * A rounded clay box with masonry UVs baked in.
 *
 * Separate from `clayGeometry` because that one *shares* geometries between
 * every mesh of the same size, and these have to be mutated per building. There
 * are only a couple of dozen in the town, so the lost sharing costs nothing.
 */
export function courseGeometry(
  w: number,
  h: number,
  d: number,
  cellW: number,
  cellH: number,
  swap = false,
): THREE.BufferGeometry {
  const geo = clayGeometry(w, h, d).clone();
  paintBox(geo, w, h, d, cellW, cellH, swap);
  return geo;
}

/** A hipped roof: four tiled triangles rising to a shared apex. */
export function hipGeometry(
  w: number,
  d: number,
  height: number,
  cellW: number,
  cellH: number,
): THREE.BufferGeometry {
  const { positions, uvs } = hipTriangles(w, d, height, cellW, cellH);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.computeVertexNormals();
  return geo;
}

/**
 * The size of one unit of masonry, shared by roof tiles and wall bricks so the
 * two read at a single scale. Spread into the `cellW, cellH` pair every
 * painting call takes.
 */
export const MASONRY_CELL: readonly [number, number] = [
  BUILDINGS.MASONRY_WIDTH,
  BUILDINGS.MASONRY_COURSE,
];

/**
 * The size of one paving slab, in world units.
 *
 * Much larger than a brick: a paving slab really is, and the ground is seen from
 * further away than a wall is, so a brick-sized unit down there vanishes into
 * grain. Sized off the footway width so a whole number of slabs fits across it.
 */
export const PAVING_CELL = PAVEMENT.WIDTH / 2;

/**
 * The size of one cobble sett, in world units.
 *
 * Half a paving slab each way, so four setts cover one — a whole-number ratio,
 * which matters because a lane and a footway meet end-on and a fractional one
 * would cut the pattern mid-sett exactly where the eye is comparing them. It
 * still divides the lane's width a whole number of times, since a lane is a
 * pavement wide by definition.
 */
export const COBBLE_CELL = PAVING_CELL / 2;
